/*! tools-catalog.js — window.ArtSeeCatalog
 *  The one data layer every free-tool page uses. Vanilla ES2017, no dependencies.
 *
 *  The catalogue is 35k products, so it is not embedded in Liquid: it lives in three
 *  Shopify Files (vertical / horizontal / square) plus a meta file, and a tool only pays
 *  for the shard it needs. URLs are injected by snippets/tools-catalog-index.liquid as
 *  window.ArtSeeCatalogUrls, so the file_url stays a Liquid concern.
 *
 *  Row layout (CONTRATO-DADOS.md §1), indexes are the public contract:
 *    0 h handle · 1 t title · 2 a artist · 3 r ratio w/h · 4 p min price
 *    5 y style · 6 s subjects "|"-joined · 7 j period · 8 e orientation · 9 z size-set id
 *    10 img image path · 11 c "hex:pct,…"
 */
(function (w) {
  'use strict';

  var IDX = { h: 0, t: 1, a: 2, r: 3, p: 4, y: 5, s: 6, j: 7, e: 8, z: 9, img: 10, c: 11 };
  var SHARDS = ['v', 'h', 's'];
  var cache = {};      // shard letter -> Promise<rows>
  var metaP = null;    // Promise<meta>
  // imageUrl() and sizes() are called inside render loops, where returning a Promise would
  // force every caller into async template code. meta() is therefore also kept
  // synchronously once resolved, and every public entry point primes it first.
  var metaSync = null;

  function urls() {
    var u = w.ArtSeeCatalogUrls;
    if (!u) throw new Error('ArtSeeCatalog: window.ArtSeeCatalogUrls is missing. Render {% render \'tools-catalog-index\' %} before this script');
    return u;
  }

  // Every failure path lands here so a tool shows one honest message instead of a blank
  // panel: a shard that 404s, a CDN that returns HTML, a truncated body.
  function getJSON(url, what) {
    if (!url) return Promise.reject(new Error('ArtSeeCatalog: no URL for ' + what));
    return fetch(url, { credentials: 'omit' }).then(function (r) {
      if (!r.ok) throw new Error('ArtSeeCatalog: ' + what + ' failed with HTTP ' + r.status);
      return r.json().catch(function () {
        throw new Error('ArtSeeCatalog: ' + what + ' is not valid JSON');
      });
    }, function (e) {
      throw new Error('ArtSeeCatalog: ' + what + ' could not be fetched (' + e.message + ')');
    });
  }

  function loadShard(k) {
    if (!cache[k]) {
      cache[k] = getJSON(urls()[k], 'shard ' + k).then(function (j) {
        if (!j || !Array.isArray(j.rows)) throw new Error('ArtSeeCatalog: shard ' + k + ' has no rows');
        return j.rows;
      });
      cache[k].catch(function () { delete cache[k]; }); // let a transient failure be retried
    }
    return cache[k];
  }

  function load(which) {
    which = which || 'all';
    if (which === 'all') {
      return Promise.all(SHARDS.map(loadShard)).then(function (parts) {
        return parts[0].concat(parts[1], parts[2]);
      });
    }
    if (SHARDS.indexOf(which) < 0) return Promise.reject(new Error('ArtSeeCatalog: unknown shard "' + which + '"'));
    return loadShard(which);
  }

  function meta() {
    if (!metaP) {
      metaP = getJSON(urls().meta, 'meta');
      metaP.catch(function () { metaP = null; });
    }
    return metaP;
  }

  /* ------------------------------------------------------------------ search */

  // Accents are folded away: the catalogue is full of "Naïve Art", "Édouard Manet",
  // "Alexandre Cabanel": a buyer types "naive" and "edouard".
  function norm(s) {
    var t = String(s == null ? '' : s).toLowerCase();
    return t.normalize ? t.normalize('NFD').replace(/[\u0300-\u036f]/g, '') : t;
  }

  // Rank, best first: exact title, title starts with the query, a *word* in title or
  // artist starts with it, then a bare substring. Without the word-boundary tier,
  // "monet" ranks "Demonetised" above "Claude Monet", which is the whole point of ranking.
  function scoreRow(row, q) {
    var t = norm(row[IDX.t]), a = norm(row[IDX.a]);
    if (t === q) return 100;
    if (t.indexOf(q) === 0) return 90;
    if (a.indexOf(q) === 0) return 80;
    if (new RegExp('\\b' + q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).test(t)) return 70;
    if (new RegExp('\\b' + q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).test(a)) return 60;
    if (t.indexOf(q) > 0) return 40;
    if (a.indexOf(q) > 0) return 30;
    return 0;
  }

  function search(q, opts) {
    opts = opts || {};
    var limit = opts.limit || 20;
    var needle = norm(q).trim();
    if (!needle) return Promise.resolve([]);
    return load(opts.orientation || 'all').then(function (rows) {
      var hits = [];
      for (var i = 0; i < rows.length; i++) {
        var s = scoreRow(rows[i], needle);
        if (s) hits.push([s, rows[i]]);
      }
      hits.sort(function (x, y) {
        return y[0] - x[0] || x[1][IDX.t].length - y[1][IDX.t].length;
      });
      return hits.slice(0, limit).map(function (p) { return p[1]; });
    });
  }

  /* ------------------------------------------------------------------ filter */

  // Deterministic 32-bit hash; a seeded shuffle has to give the same "surprise me" list
  // for the same #hash, otherwise sharing a result URL shows a different set.
  function hash32(str) {
    var h = 2166136261;
    for (var i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = Math.imul(h, 16777619); }
    return h >>> 0;
  }

  function filter(o) {
    o = o || {};
    var limit = o.limit == null ? 24 : o.limit;
    return load(o.orientation || 'all').then(function (rows) {
      var out = [];
      for (var i = 0; i < rows.length; i++) {
        var r = rows[i];
        if (o.ratioMin != null && r[IDX.r] < o.ratioMin) continue;
        if (o.ratioMax != null && r[IDX.r] > o.ratioMax) continue;
        if (o.style && r[IDX.y] !== o.style) continue;
        if (o.period && r[IDX.j] !== o.period) continue;
        if (o.sizeSet != null && r[IDX.z] !== o.sizeSet) continue;
        if (o.subject && ('|' + r[IDX.s] + '|').indexOf('|' + o.subject + '|') < 0) continue;
        if (o.priceMin != null && r[IDX.p] < o.priceMin) continue;
        if (o.priceMax != null && r[IDX.p] > o.priceMax) continue;
        out.push(r);
      }
      if (o.seed != null) {
        var seed = hash32(String(o.seed));
        out = out.map(function (r) { return [hash32(r[IDX.h] + ':' + seed), r]; })
          .sort(function (a, b) { return a[0] - b[0]; })
          .map(function (p) { return p[1]; });
      }
      return limit ? out.slice(0, limit) : out;
    });
  }

  /* ------------------------------------------------------- colour similarity */

  function hexToRgb(hex) {
    var s = String(hex).replace('#', '');
    if (s.length === 3) s = s[0] + s[0] + s[1] + s[1] + s[2] + s[2];
    var n = parseInt(s, 16);
    if (isNaN(n) || s.length !== 6) return null;
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }

  // sRGB -> CIE Lab (D65). Mirrors scripts/tools/color-names.mjs; the two must agree or
  // a swatch named offline stops matching the neighbours found in the browser.
  function rgbToLab(r, g, b) {
    function f(u) { u /= 255; return u <= 0.04045 ? u / 12.92 : Math.pow((u + 0.055) / 1.055, 2.4); }
    var R = f(r), G = f(g), B = f(b);
    var x = (R * 0.4124564 + G * 0.3575761 + B * 0.1804375) / 0.95047;
    var y = (R * 0.2126729 + G * 0.7151522 + B * 0.0721750);
    var z = (R * 0.0193339 + G * 0.1191920 + B * 0.9503041) / 1.08883;
    function t(u) { return u > 0.008856 ? Math.pow(u, 1 / 3) : 7.787 * u + 16 / 116; }
    x = t(x); y = t(y); z = t(z);
    return [116 * y - 16, 500 * (x - y), 200 * (y - z)];
  }

  function parsePalette(c) {
    var out = [];
    if (!c) return out;
    var parts = String(c).split(',');
    for (var i = 0; i < parts.length; i++) {
      var kv = parts[i].split(':');
      var rgb = hexToRgb(kv[0]);
      if (!rgb) continue;
      out.push({ hex: kv[0], pct: Number(kv[1]) || 0, lab: rgbToLab(rgb[0], rgb[1], rgb[2]) });
    }
    return out;
  }

  function dLab(a, b) {
    var dL = a[0] - b[0], da = a[1] - b[1], db = a[2] - b[2];
    return Math.sqrt(dL * dL + da * da + db * db);
  }

  // Distance = for each colour the visitor picked, the closest colour in the painting,
  // weighted by how much of the painting that colour covers. A picked colour that only
  // appears in a 2% corner should not score like one that covers half the canvas.
  function paletteDistance(want, have) {
    if (!have.length) return Infinity;
    var total = 0;
    for (var i = 0; i < want.length; i++) {
      var best = Infinity, bestPct = 0;
      for (var j = 0; j < have.length; j++) {
        var d = dLab(want[i], have[j].lab);
        if (d < best) { best = d; bestPct = have[j].pct; }
      }
      total += best * (1 - Math.min(bestPct, 60) / 120);
    }
    return total / want.length;
  }

  function similarColor(hexList, opts) {
    opts = opts || {};
    var limit = opts.limit == null ? 24 : opts.limit;
    var list = (Array.isArray(hexList) ? hexList : [hexList]).map(function (h) {
      var rgb = hexToRgb(h);
      return rgb ? rgbToLab(rgb[0], rgb[1], rgb[2]) : null;
    }).filter(Boolean);
    if (!list.length) return Promise.reject(new Error('ArtSeeCatalog: similarColor needs at least one valid hex colour'));
    return load(opts.orientation || 'all').then(function (rows) {
      var scored = [];
      for (var i = 0; i < rows.length; i++) {
        var pal = parsePalette(rows[i][IDX.c]);
        if (!pal.length) continue;
        scored.push([paletteDistance(list, pal), rows[i]]);
      }
      scored.sort(function (a, b) { return a[0] - b[0]; });
      return scored.slice(0, limit).map(function (p) { return p[1]; });
    });
  }

  /* --------------------------------------------------------------- utilities */

  function imageUrl(row, width) {
    width = width || 400;
    var base = (metaSync && metaSync.imgBase) || 'https://art-and-see.com/cdn/shop/files/';
    var img = row && row[IDX.img];
    if (!img) return '';
    return base + img + (img.indexOf('?') < 0 ? '?' : '&') + 'width=' + width;
  }

  function pdpUrl(row, opts) {
    opts = opts || {};
    var u = '/products/' + row[IDX.h];
    if (opts.size) u += '#size=' + String(opts.size).replace(/\s/g, '');
    return u;
  }

  function sizes(setId) {
    var sets = metaSync && metaSync.sizeSets;
    var list = sets && sets[String(setId)];
    if (!list) return [];
    return list.map(function (s) {
      return { w: s.w, h: s.h, wcm: s.wcm, hcm: s.hcm, label: s.w + '" × ' + s.h + '"' };
    });
  }

  function track(event, params) {
    try {
      w.dataLayer = w.dataLayer || [];
      var o = { event: event };
      for (var k in params) if (Object.prototype.hasOwnProperty.call(params, k)) o[k] = params[k];
      w.dataLayer.push(o);
    } catch (e) { /* analytics must never break a tool */ }
  }

  function primeMeta() { return meta().then(function (m) { metaSync = m; return m; }); }

  w.ArtSeeCatalog = {
    IDX: IDX,
    load: function (which) { return primeMeta().then(function () { return load(which); }); },
    meta: primeMeta,
    search: function (q, o) { return primeMeta().then(function () { return search(q, o); }); },
    filter: function (o) { return primeMeta().then(function () { return filter(o); }); },
    similarColor: function (list, o) { return primeMeta().then(function () { return similarColor(list, o); }); },
    imageUrl: imageUrl,
    pdpUrl: pdpUrl,
    sizes: sizes,
    track: track,
    // exposed for the tools that draw swatches and for the unit tests
    parsePalette: parsePalette,
    rgbToLab: rgbToLab,
    hexToRgb: hexToRgb,
    _reset: function () { cache = {}; metaP = null; metaSync = null; }
  };
})(window);
